import { json, LoaderFunction, MetaFunction } from "@remix-run/node";
import { useLoaderData, useLocation } from "@remix-run/react";
import { redirect } from "@vercel/remix";

import { Avatar } from "../components/Avatar";
import { Link } from "../components/Button/Link";
import { Pill } from "../components/Pill";
import { UserArtistGrid } from "../components/UserArtistGrid/UserArtistGrid";
import { UserPageFooter } from "../components/UserPageFooter";
import { UserPageLoginPopover } from "../components/UserPageLoginPopover";
import { withMiddleEllipsis } from "../components/utils";
import { getArtistsByUserId } from "../models/artist.server";
import { getFirstEthAddressByUserId } from "../models/identityVerification.server";
import { getMatchByUserIds } from "../models/match.server";
import { getUserByIdOrUsername, isUserOnboarded } from "../models/user.server";
import { getUserId } from "../session.server";
import { ArtistDTO } from "../types/artist";
import { FeedMatchDTO } from "../types/feed-match";
import { UserDTO } from "../types/user";
import {
  buildMetaTags,
  buildOgProfileUrl,
  useOptionalMatchCount,
  useOptionalUser,
} from "../utils";

interface LoaderData {
  user: UserDTO;
  artists: ArtistDTO[];
  match: FeedMatchDTO | null;
  ethAddress?: string;
}

export const loader: LoaderFunction = async ({ request, params }) => {
  const currentUserId = await getUserId(request);

  const { idOrUsername } = params;

  if (!idOrUsername) {
    throw new Response("User id missing from url", {
      status: 400,
    });
  }

  const user = await getUserByIdOrUsername(idOrUsername);

  if (!user) {
    throw new Response("User not found", {
      status: 404,
    });
  }

  const artists = await getArtistsByUserId(user.id);

  const url = new URL(request.url);
  const origin = url.origin;

  const ogImageUrl = buildOgProfileUrl({
    apiUrl: origin,
    userId: user.id,
  });

  if (!currentUserId) {
    return json({
      user,
      ogImageUrl,
      artists,
      url: url.toString(),
    });
  }

  const match = await getMatchByUserIds(currentUserId, user.id);

  const isCurrentUserOnboarded = await isUserOnboarded(currentUserId);

  if (!isCurrentUserOnboarded) {
    const currentUrlPath = url.pathname;
    return redirect(`/my-artists?redirect=${currentUrlPath}`);
  }

  const ethAddress = await getFirstEthAddressByUserId(user.id);

  return json({
    user,
    match,
    artists,
    ogImageUrl,
    ethAddress,
    url: url.toString(),
  });
};

export const meta: MetaFunction<typeof loader> = ({ data }) =>
  buildMetaTags({
    routeName: data.user?.username,
    description: data.user?.bio,
    imageUrl: data.ogImageUrl,
    url: data.url,
  });

export default function UserPage() {
  const { user, match, artists, ethAddress } = useLoaderData<LoaderData>();
  const matchCount = useOptionalMatchCount();
  const currentUser = useOptionalUser();
  const isCurrentUser = currentUser?.id === user.id;
  const location = useLocation();
  return (
    <>
      <div className="px-4 flex flex-col h-full">
        <div className="flex items-center gap-4 py-4">
          <Avatar
            imageUrl={user.pfpUrl}
            name={user.username}
            className="w-[4.5rem] h-[4.5rem] border-none"
          />
          <div className="gap-1.5 flex flex-col">
            <h1 className="text-white">{user.username}</h1>
            {user.bio && user.bio.length > 0 ? (
              <p className="text-gray-400">{user.bio}</p>
            ) : null}
            {ethAddress ? (
              <Pill>{withMiddleEllipsis(ethAddress, 10)}</Pill>
            ) : null}
          </div>
        </div>
        <div className="pt-6">
          <div className="pt-3 pb-1 flex justify-between items-center">
            <span className="text-white">top artists</span>
            {match ? (
              <span className="text-primary">
                {match.commonArtists.length} in common
              </span>
            ) : currentUser?.id === user.id ? (
              <Link
                prefetch="intent"
                to={`/my-artists?redirect=/${user.username}`}
                className="px-1"
              >
                {artists.length === 8 ? "edit" : "add more artists"}
              </Link>
            ) : null}
          </div>
          <UserArtistGrid
            artists={artists}
            isCurrentUser={isCurrentUser}
            isMatch={!!match}
            matchArtists={match?.commonArtists}
          />
          {match ? (
            <div className="pt-8 flex justify-center">
              <Link to={`/match/${user.id}?sendPoke=true`} variant="solid">
                Poke 👉
              </Link>
            </div>
          ) : currentUser && !isCurrentUser ? (
            <div className="flex items-center pt-10 h-48 justify-center">
              <Link variant="outline" to="/">
                {matchCount
                  ? `view your ${matchCount} matches`
                  : "view your matches"}
              </Link>
            </div>
          ) : null}
        </div>
        {isCurrentUser ? (
          <UserPageFooter className="grow shrink-0 content-end" user={user} />
        ) : null}
      </div>
      {!currentUser ? (
        <UserPageLoginPopover to={`/login?redirect=${location.pathname}`} />
      ) : null}
    </>
  );
}
